export type Cadence = "monthly" | "annual";
export interface Subscription {
  subscription_id: string; cadence: Cadence; provider_status: string; paid: boolean;
  cancel_at_period_end: boolean; cancel_at: string | null; current_period_end: string | null; paid_through: string | null;
  loyaltyTier?: number; renewalAmountMinor?: number; billedAmountMinor?: number | null; loyaltyReviewRequired?: boolean;
}
export interface RecordAccess {
  paid: boolean; recordsAvailable: boolean; paidEntitlementEndsAt: string | null; viewingEndsAt: string | null; expiryReminder: boolean;
  trialActive?: boolean; trialEndsAt?: string; trialViewingEndsAt?: string; accessKind?: "paid" | "trial" | "viewing" | "none"; workspaceErased?: boolean;
}
export interface BillingObservation {
  testMode: boolean; purchaseAvailable: boolean; accessEnforced: boolean; paid: boolean; subscriptions: Subscription[];
  checkoutPending: { cadence: Cadence; request_key: string; expires_at: string; amountMinor?:number } | null;
  recordAccess?: RecordAccess;
  loyaltyEnabled?: boolean;
  checkoutQuote?: { tier: number; monthlyMinor: number; annualMinor: number };
}
export class BillingRequestError extends Error {
  constructor(public status: number) { super(status === 409 ? "A subscription or Checkout already exists. Refresh billing before trying again." : "Billing could not be loaded. Please retry."); }
}
export async function billingRequest<T>(getToken: () => Promise<string | null>, operation: "status" | "reconcile" | "checkout" | "portal", body?: unknown, signal?: AbortSignal): Promise<T> {
  const token = await getToken();
  if (!token) throw new Error("Sign in again to view subscription billing.");
  const response = await fetch(`/api/subscriptions/${operation}`, { method: operation === "status" ? "GET" : "POST", signal,
    headers: { authorization: `Bearer ${token}`, ...(body ? { "Content-Type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined });
  if (!response.ok) throw new BillingRequestError(response.status);
  return response.json() as Promise<T>;
}
export function subscriptionSummary(observation: BillingObservation) {
  const blocking = observation.subscriptions.filter(s => !["canceled", "incomplete_expired"].includes(s.provider_status));
  // Historical subscription IDs do not define lifecycle order. Once every
  // subscription is terminal, show the most recent verified billing period.
  const periodEnd = (subscription: Subscription) => Date.parse(subscription.current_period_end ?? subscription.paid_through ?? "") || 0;
  const latest = observation.subscriptions.reduce<Subscription | null>((current, subscription) =>
    !current || periodEnd(subscription) > periodEnd(current) ? subscription : current, null);
  return { hasSubscription: blocking.length > 0, current: blocking[0] ?? latest,
    canPurchase: observation.purchaseAvailable && blocking.length === 0 && !observation.checkoutPending &&
      !observation.subscriptions.some(subscription => subscription.loyaltyReviewRequired) };
}
export function providerDestination(value: string, kind: "checkout" | "portal") {
  const url = new URL(value);
  if (url.origin !== (kind === "checkout" ? "https://checkout.stripe.com" : "https://billing.stripe.com") || url.username || url.password) throw new Error("The billing destination could not be verified. Please retry.");
  return url.href;
}
